import { Capacitor } from '@capacitor/core';
import type { LiveUpdateCheckResponseDto } from '@supply-mover-2-0/shared';
import { ApiService } from '@supply-mover-2-0/core';

/**
 * PD-13933 — live updates of the native apps' web layer.
 *
 * The native counterpart of AppUpdateService (which no-ops in a native
 * shell): it asks crm-api whether a newer web bundle exists for THIS
 * native shell and, if so, has the on-device updater
 * (@capgo/capacitor-updater, manual mode) download, verify and stage it.
 *
 * - A normal bundle is downloaded and verified in the background, then
 *   applied at the start of the NEXT launch: the launch check asks the
 *   server again and, if it still offers that bundle, switches to it while
 *   the splash is still up. Asking first means a bundle that was paused or
 *   rolled back after it downloaded is never applied. (The updater's own
 *   `next()` is deliberately not used: it applies when the app is merely
 *   sent to the background, which reloads it behind a user who switched
 *   away for a moment in the middle of a task.)
 * - The banner's "Restart now" applies the staged bundle at once, but only
 *   after the app has been network-quiet for a moment, the same rule
 *   AppUpdateService uses, so nobody loses a half-submitted form.
 * - A bundle marked critical applies as soon as it is downloaded.
 * - The updater rolls a bundle back automatically if the app does not
 *   report ready within its appReadyTimeout. Ready is reported as soon as
 *   this code runs (start() → notifyAppReady), which proves the bundle
 *   loaded and executed; it deliberately does not wait for the network,
 *   so a good bundle is never rolled back just because the phone is
 *   offline.
 *
 * Everything fails quiet: no plugin (an older binary), no stamp, a failed
 * check or download — the app keeps running the code it has.
 */

export interface LiveUpdateStampModel {
	schemaVersion: 1;
	appId: string;
	environment: string;
	bundleId: string;
	gitSha: string;
	builtAt: string;
	nativeFingerprint: string;
}

export interface LiveUpdateStateModel {
	/** The bundle the app is running now (null until read, or on web). */
	running: LiveUpdateStampModel | null;
	/** True when the running code is a downloaded bundle, not the built-in one. */
	runningDownloaded: boolean;
	/** The updater's device id, shown so a tester can be added to a channel. */
	deviceId: string | null;
	/** A verified bundle is staged and will apply on the next cold launch. */
	readyBundleId: string | null;
	/** "Restart now" was pressed and is waiting for a quiet moment. */
	applyPending: boolean;
	/** The last server answer, for the support details screen. */
	lastReason: LiveUpdateCheckResponseDto['reason'] | null;
	/** The offered bundle could not be downloaded or did not verify. */
	downloadFailed: boolean;
}

type Listener = (state: LiveUpdateStateModel) => void;

/** Minimal surface of the updater plugin this service uses. */
interface UpdaterBundleModel {
	id: string;
	version: string;
	status: string;
}
interface UpdaterPluginModel {
	notifyAppReady(): Promise<unknown>;
	current(): Promise<{ bundle: UpdaterBundleModel; native: string }>;
	list(): Promise<{ bundles: Array<UpdaterBundleModel> }>;
	getDeviceId(): Promise<{ deviceId: string }>;
	download(options: {
		url: string;
		version: string;
		checksum: string;
		sessionKey: string;
	}): Promise<UpdaterBundleModel>;
	set(options: { id: string }): Promise<void>;
}

const MIN_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const QUIET_MS = 3 * 1000;
const QUIET_POLL_MS = 500;
const STAMP_PATH = '/live-update.json';
/** Built-in stamp per native build, recorded while the built-in bundle runs. */
const BUILTIN_KEY = 'workd:live-update:builtin';
/**
 * The bundle id being switched to, written just before set(). If the next
 * start is NOT running it, the updater rolled it back (it never reported
 * ready), so it is recorded as failed. The updater deletes a rolled-back
 * bundle, so its own bundle list cannot be relied on to remember it.
 */
const APPLYING_KEY = 'workd:live-update:applying';
/** Bundle ids that failed to start on this device; reported on every check. */
const FAILED_KEY = 'workd:live-update:failed';
const FAILED_LIMIT = 20;
/**
 * Download/verification failures per bundle id. A bundle that fails
 * verification fails every time (tampered, or signed with another key), so
 * after this many attempts it is recorded as failed instead of being
 * downloaded again on every check; a passing network blip stays below it.
 */
const DOWNLOAD_FAILURES_KEY = 'workd:live-update:download-failures';
const DOWNLOAD_ATTEMPTS = 3;

function recordFailed(bundleId: string): void {
	try {
		const failed = [bundleId, ...readFailedIds().filter((id) => id !== bundleId)].slice(0, FAILED_LIMIT);
		localStorage.setItem(FAILED_KEY, JSON.stringify(failed));
	} catch {
		// Storage unavailable — the bundle may be tried again later.
	}
}

function readFailedIds(): Array<string> {
	try {
		const raw = localStorage.getItem(FAILED_KEY);
		const parsed: unknown = raw != null ? JSON.parse(raw) : [];
		return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
	} catch {
		return [];
	}
}

interface BuiltinRecordModel {
	native: string;
	stamp: LiveUpdateStampModel;
}

/** Reduce either VITE_API_URL form (bare origin or origin + /api) to the origin. */
function normalizeApiOrigin(apiBaseUrl: string): string {
	return apiBaseUrl.replace(/\/+$/, '').replace(/\/api$/, '');
}

function isStamp(value: unknown): value is LiveUpdateStampModel {
	if (typeof value !== 'object' || value == null) {
		return false;
	}
	const record = value as Partial<Record<keyof LiveUpdateStampModel, unknown>>;
	return (
		record.schemaVersion === 1 &&
		typeof record.bundleId === 'string' &&
		typeof record.gitSha === 'string' &&
		typeof record.builtAt === 'string' &&
		typeof record.nativeFingerprint === 'string' &&
		record.nativeFingerprint !== ''
	);
}

class LiveUpdateServiceClass {
	private _state: LiveUpdateStateModel = {
		running: null,
		runningDownloaded: false,
		deviceId: null,
		readyBundleId: null,
		applyPending: false,
		lastReason: null,
		downloadFailed: false,
	};
	private readonly _listeners = new Set<Listener>();
	private _updater: UpdaterPluginModel | null = null;
	private _apiOrigin = '';
	private _started = false;
	private _checking = false;
	private _lastCheckAt = 0;
	/** Updater-internal id of the staged bundle (what next/set take). */
	private _readyUpdaterId: string | null = null;
	private _quietTimer: ReturnType<typeof setInterval> | null = null;
	/** Native version the updater reports; a built-in record from another build is ignored. */
	private _native: string | null = null;

	/**
	 * Start once from the app shell. Reports ready to the updater first —
	 * before anything that can fail or wait — then runs the first check.
	 */
	public async start(options: { apiBaseUrl: string }): Promise<void> {
		if (this._started || !Capacitor.isNativePlatform()) {
			return;
		}
		this._started = true;
		this._apiOrigin = normalizeApiOrigin(options.apiBaseUrl);
		if (!Capacitor.isPluginAvailable('CapacitorUpdater')) {
			// A binary from before live updates — nothing to do.
			return;
		}
		try {
			const mod = await import('@capgo/capacitor-updater');
			this._updater = mod.CapacitorUpdater as unknown as UpdaterPluginModel;
			await this._updater.notifyAppReady();
		} catch {
			this._updater = null;
			return;
		}

		await this._readRunning();
		void this.checkNow(true, true);

		try {
			const { App } = await import('@capacitor/app');
			await App.addListener('appStateChange', (state) => {
				if (state.isActive) {
					void this.checkNow();
				}
			});
		} catch {
			// No App plugin — the launch-time check already ran.
		}
	}

	public getState(): LiveUpdateStateModel {
		return this._state;
	}

	public subscribe(listener: Listener): () => void {
		this._listeners.add(listener);
		return () => {
			this._listeners.delete(listener);
		};
	}

	/** The banner's "Restart now": apply the staged bundle at the next quiet moment. */
	public applyNow(): void {
		if (this._readyUpdaterId == null || this._updater == null || this._quietTimer != null) {
			return;
		}
		this._setState({ applyPending: true });
		this._quietTimer = setInterval(() => {
			if (Date.now() - ApiService.lastRequestAt < QUIET_MS) {
				return;
			}
			if (this._quietTimer != null) {
				clearInterval(this._quietTimer);
				this._quietTimer = null;
			}
			const id = this._readyUpdaterId;
			const bundleId = this._state.readyBundleId;
			if (id != null && bundleId != null) {
				void this._apply(id, bundleId);
			}
		}, QUIET_POLL_MS);
	}

	/**
	 * @param force skip the five-minute spacing between checks
	 * @param isLaunch the first check of a fresh JavaScript context; an offer
	 *   that is already downloaded is applied immediately then
	 */
	public async checkNow(force = false, isLaunch = false): Promise<void> {
		const updater = this._updater;
		const running = this._state.running;
		if (updater == null || running == null || this._checking) {
			return;
		}
		if (!force && Date.now() - this._lastCheckAt < MIN_CHECK_INTERVAL_MS) {
			return;
		}
		this._checking = true;
		this._lastCheckAt = Date.now();
		try {
			const [{ App }, { bundles }] = await Promise.all([import('@capacitor/app'), updater.list()]);
			const info = await App.getInfo();
			const builtin = this._readBuiltinRecord();
			const failed = [
				...new Set([
					...readFailedIds(),
					...bundles.filter((bundle) => bundle.status === 'error').map((bundle) => bundle.version),
				]),
			];

			const params = new URLSearchParams({
				appId: info.id,
				platform: Capacitor.getPlatform() === 'ios' ? 'ios' : 'android',
				nativeVersion: `${info.version} (${info.build})`,
				// The shell's own fingerprint: from the built-in stamp when we
				// have it, else from the running bundle (which only ever runs
				// on a shell with the same fingerprint).
				fingerprint: builtin?.stamp.nativeFingerprint ?? running.nativeFingerprint,
				bundleId: running.bundleId,
				gitSha: running.gitSha,
				deviceId: this._state.deviceId ?? '',
				failed: failed.join(','),
			});
			if (builtin != null) {
				params.set('builtinBuiltAt', builtin.stamp.builtAt);
			}
			const response = await fetch(`${this._apiOrigin}/api/mobile-apps/live-update?${params.toString()}`, {
				cache: 'no-store',
			});
			if (!response.ok) {
				return;
			}
			const dto = (await response.json()) as LiveUpdateCheckResponseDto;
			this._setState({ lastReason: dto.reason });
			if (dto.status !== 'available' || dto.offer == null) {
				return;
			}
			const { offer } = dto;
			if (this._state.readyBundleId === offer.bundleId) {
				return;
			}

			// A copy downloaded and verified by an earlier session.
			const existing = bundles.find(
				(bundle) => bundle.version === offer.bundleId && bundle.status !== 'error',
			);
			if (failed.includes(offer.bundleId)) {
				// Belt and braces: the server already skips ids we report.
				return;
			}
			if (existing != null && isLaunch) {
				// Staged last session and still offered: apply now, while the
				// app is starting.
				await this._apply(existing.id, offer.bundleId);
				return;
			}
			let downloaded: UpdaterBundleModel;
			if (existing != null) {
				downloaded = existing;
			} else {
				try {
					downloaded = await updater.download({
						url: offer.url,
						version: offer.bundleId,
						checksum: offer.checksum,
						sessionKey: offer.sessionKey,
					});
				} catch {
					// The updater deletes a download whose signature or checksum
					// does not match. Count it, and stop retrying a bundle that
					// keeps failing.
					this._countDownloadFailure(offer.bundleId);
					this._setState({ downloadFailed: true });
					return;
				}
			}
			this._setState({ downloadFailed: false });

			if (offer.critical) {
				await this._apply(downloaded.id, offer.bundleId);
				return;
			}
			// Staged: applies at the start of the next launch, or now via the
			// banner's "Restart now".
			this._readyUpdaterId = downloaded.id;
			this._setState({ readyBundleId: offer.bundleId });
		} catch {
			// Offline, endpoint down, download or verification failed — keep
			// running the current code; the next check tries again.
		} finally {
			this._checking = false;
		}
	}

	private async _readRunning(): Promise<void> {
		const updater = this._updater;
		if (updater == null) {
			return;
		}
		try {
			const [stampResponse, current, device] = await Promise.all([
				fetch(STAMP_PATH, { cache: 'no-store' }),
				updater.current(),
				updater.getDeviceId(),
			]);
			const stamp: unknown = stampResponse.ok ? await stampResponse.json() : null;
			if (!isStamp(stamp)) {
				// A bundle built without the stamp: we cannot say which shell
				// this is, so the app never asks for an update.
				return;
			}
			this._native = current.native;
			this._settleApplying(stamp.bundleId);
			const runningDownloaded = current.bundle.id !== 'builtin';
			if (!runningDownloaded) {
				this._writeBuiltinRecord({ native: current.native, stamp });
			}
			this._setState({ running: stamp, runningDownloaded, deviceId: device.deviceId });
		} catch {
			// Leave running null — no checks without knowing what runs.
		}
	}

	/**
	 * Switch to a downloaded bundle. set() reloads the WebView, so the id is
	 * recorded first; the next start settles it (see _settleApplying).
	 */
	private async _apply(updaterId: string, bundleId: string): Promise<void> {
		if (this._updater == null) {
			return;
		}
		try {
			localStorage.setItem(APPLYING_KEY, bundleId);
		} catch {
			// Storage unavailable — a failed bundle may then be offered again
			// after its rollback; the server-side pause/rollback still applies.
		}
		await this._updater.set({ id: updaterId });
	}

	private _countDownloadFailure(bundleId: string): void {
		try {
			const raw = localStorage.getItem(DOWNLOAD_FAILURES_KEY);
			const parsed: unknown = raw != null ? JSON.parse(raw) : {};
			const counts: Record<string, number> =
				typeof parsed === 'object' && parsed != null && !Array.isArray(parsed)
					? (parsed as Record<string, number>)
					: {};
			const count = (typeof counts[bundleId] === 'number' ? counts[bundleId] : 0) + 1;
			if (count >= DOWNLOAD_ATTEMPTS) {
				recordFailed(bundleId);
				delete counts[bundleId];
			} else {
				counts[bundleId] = count;
			}
			localStorage.setItem(DOWNLOAD_FAILURES_KEY, JSON.stringify(counts));
		} catch {
			// Storage unavailable — the next check simply tries again.
		}
	}

	/** Called on every start with the bundle actually running. */
	private _settleApplying(runningBundleId: string): void {
		try {
			const applying = localStorage.getItem(APPLYING_KEY);
			if (applying == null) {
				return;
			}
			localStorage.removeItem(APPLYING_KEY);
			if (applying !== runningBundleId) {
				// We switched to it and are not running it: it was rolled back.
				recordFailed(applying);
			}
		} catch {
			// Storage unavailable — nothing to settle.
		}
	}

	private _readBuiltinRecord(): BuiltinRecordModel | null {
		try {
			const raw = localStorage.getItem(BUILTIN_KEY);
			if (raw == null) {
				return null;
			}
			const record = JSON.parse(raw) as Partial<BuiltinRecordModel>;
			return isStamp(record.stamp) && typeof record.native === 'string' && record.native === this._native
				? { native: record.native, stamp: record.stamp }
				: null;
		} catch {
			return null;
		}
	}

	private _writeBuiltinRecord(record: BuiltinRecordModel): void {
		try {
			localStorage.setItem(BUILTIN_KEY, JSON.stringify(record));
		} catch {
			// Storage unavailable — checks fall back to the running stamp.
		}
	}

	private _setState(changes: Partial<LiveUpdateStateModel>): void {
		this._state = { ...this._state, ...changes };
		for (const listener of this._listeners) {
			listener(this._state);
		}
	}
}

export const LiveUpdateService = new LiveUpdateServiceClass();
